import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Grid, Popover } from '@mui/material';
import StepInput from '../../../components/common/step_input';
import {
  aggregateServings,
  persistScaleMealsToServingsByKindOfPerson,
} from '../../end_user_collections/end_user_collections_slice';
import { SmorgRootState } from '../../../app/smorg_store';
import PopupTitle from '../../../components/common/popup_title';

interface ServingsModalProps {
  anchorEl: HTMLElement | null;
  isLoading: boolean;
  onSubmit: (servings: number) => void;
  onDismiss: () => void;
}

export const ServingsModal = ({
  anchorEl,
  isLoading,
  onSubmit,
  onDismiss,
}: ServingsModalProps) => {
  const dispatch = useDispatch();

  const scaleMealsTo = useSelector(
    (state: SmorgRootState) => state.endUserCollections.scaleMealsTo,
  );

  const [scaleMealsToStrs, setScaleMealsToStrs] = useState({
    adults: '',
    children: '',
  });

  useEffect(() => {
    const updatedScaleMealsToStrs = { ...scaleMealsToStrs };
    (Object.keys(scaleMealsTo) as Array<keyof typeof scaleMealsTo>).forEach(
      (key) => {
        updatedScaleMealsToStrs[key] = String(scaleMealsTo[key]);
      },
    );
    setScaleMealsToStrs(updatedScaleMealsToStrs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(scaleMealsTo)]);

  const onChangeScaleMealsTo = (field: string, value: string) => {
    setScaleMealsToStrs({
      ...scaleMealsToStrs,
      [field]: value,
    });
    const newValue = Number(value);
    if (Number.isNaN(newValue) || newValue < 1) {
      return;
    }
    const updatedScaleMealsTo = { ...scaleMealsTo, [field]: newValue };
    dispatch(persistScaleMealsToServingsByKindOfPerson(updatedScaleMealsTo));
  };

  return (
    <Popover
      open
      PaperProps={{
        style: { width: '320px', height: '190px', padding: '10px' },
      }}
      onClose={onDismiss}
      onClick={(ev) => ev.stopPropagation()}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}>
      <Grid container style={{ fontSize: '14px' }}>
        <Grid item xs={12}>
          <PopupTitle
            titleText="Add to shopping list"
            closeButtonEnabled
            onClickClose={onDismiss}
          />
        </Grid>
        <Grid item xs={12} style={{ padding: '10px 5px', display: 'flex' }}>
          <div style={{ margin: 'auto 0' }}>How many servings:</div>
        </Grid>
        <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
          <div style={{ margin: 'auto 0' }}>Adults:</div>
        </Grid>
        <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
          <div className="mealCardServingsStepInput" style={{ margin: 'auto' }}>
            <StepInput
              value={scaleMealsToStrs.adults as any as number}
              min={0}
              max={9}
              onChange={(ev) => onChangeScaleMealsTo('adults', ev.target.value)}
              inputName="adults"
            />
          </div>
        </Grid>
        <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
          <div style={{ margin: 'auto 0' }}>Children:</div>
        </Grid>
        <Grid item xs={6} style={{ padding: '5px', display: 'flex' }}>
          <div className="mealCardServingsStepInput" style={{ margin: 'auto' }}>
            <StepInput
              value={scaleMealsToStrs.children as any as number}
              min={0}
              max={9}
              onChange={(ev) =>
                onChangeScaleMealsTo('children', ev.target.value)
              }
              inputName="children"
            />
          </div>
        </Grid>
        <Grid item xs={12} style={{ padding: '15px 5px 5px' }}>
          <Button
            onClick={(ev) => {
              onSubmit(aggregateServings(scaleMealsTo));
              ev.stopPropagation();
            }}
            variant="contained"
            size="medium"
            color="primary"
            disableElevation
            disabled={aggregateServings(scaleMealsTo) <= 0 || isLoading}
            style={{ justifyContent: 'flex-start' }}>
            {isLoading ? 'Adding...' : `Add`}
          </Button>
          {isLoading && (
            <CircularProgress size={20} style={{ margin: 'auto 10px' }} />
          )}
        </Grid>
      </Grid>
    </Popover>
  );
};
